import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AiAssistantComposer,
  AiAssistantDecisionActions,
  AiAssistantMenuShell,
  AiAssistantQueue,
  AiAssistantTaskPicker,
} from '../components/index.js';
import {
  AI_QUEUE_STATUS,
  useAiRequestQueue,
} from '../utilities/aiRequestQueue.js';
import AiRecordUpdateReview from '../NewBlogSystem/components/AiRecordUpdateReview.jsx';
import {
  loadProjectsSkillsAiHistory,
  saveProjectsSkillsAiHistory,
} from './projectsSkillsAiHistory.js';
import '../assets/projectsSkillsAssistant.scss';

const TASKS = Object.freeze({
  RECOMMEND: 'recommend',
  EDIT_PROJECTS: 'edit-projects',
  EDIT_SKILLS: 'edit-skills',
  REWRITE: 'rewrite',
});
const normalizeTask = (value) => {
  if (value === 'edit') return TASKS.EDIT_PROJECTS;
  return Object.values(TASKS).includes(value) ? value : TASKS.RECOMMEND;
};
const isEditTask = (value) => (
  value === TASKS.EDIT_PROJECTS || value === TASKS.EDIT_SKILLS
);
const editScopeForTask = (value) => (
  value === TASKS.EDIT_SKILLS ? 'skills' : 'projects'
);
const DEFAULT_NEXT_STEPS_REQUEST = 'Recommend the most useful projects and skills to work on next.';
const TASK_OPTIONS = [
  { key: TASKS.RECOMMEND, label: 'Next steps', iconClass: 'bi-lightbulb', promptRequired: false },
  { key: TASKS.EDIT_PROJECTS, label: 'Edit projects', iconClass: 'bi-kanban', promptRequired: true },
  { key: TASKS.EDIT_SKILLS, label: 'Edit skills', iconClass: 'bi-award', promptRequired: true },
];
const hasRecommendations = (value) => (
  (Array.isArray(value?.projects) && value.projects.length > 0)
  || (Array.isArray(value?.skills) && value.skills.length > 0)
);
const hasUpdates = (value) => (
  (Array.isArray(value?.projectUpdates) && value.projectUpdates.length > 0)
  || (Array.isArray(value?.skillUpdates) && value.skillUpdates.length > 0)
);
const newMessage = (role, content, options = {}) => ({
  id: `projects-skills-ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  role,
  content,
  createdAt: new Date().toISOString(),
  ...options,
});

const Recommendations = ({ value, onAddProject, onAddSkill }) => {
  if (!hasRecommendations(value)) {
    return <p className="text-muted small mb-0">No additional suggestions were found.</p>;
  }
  return (
    <div className="projects-skills-ai-results">
      {value.projects?.length ? <section>
        <h3>Project ideas</h3>
        <div className="projects-skills-ai-result-list">
          {value.projects.map((item, index) => <article key={`${item.title}-${index}`}>
            <i className="bi bi-kanban" aria-hidden="true"></i>
            <div>
              <strong>{item.title}</strong>
              <p>{item.overview}</p>
              {item.why ? <small><b>Why it helps:</b> {item.why}</small> : null}
              {Array.isArray(item.steps) && item.steps.length ? (
                <ol className="projects-skills-ai-project-steps" aria-label={`${item.title} project plan`}>
                  {item.steps.map((step, stepIndex) => <li key={`${step}-${stepIndex}`}>{step}</li>)}
                </ol>
              ) : null}
              {item.outcome ? <small><b>Finished when:</b> {item.outcome}</small> : null}
              <button className="btn btn-sm btn-outline-primary mt-2" type="button" onClick={() => onAddProject(item)}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>Add project
              </button>
            </div>
          </article>)}
        </div>
      </section> : null}
      {value.skills?.length ? <section>
        <h3>Skill ideas</h3>
        <div className="projects-skills-ai-result-list">
          {value.skills.map((item, index) => <article key={`${item.name}-${index}`}>
            <i className="bi bi-award" aria-hidden="true"></i>
            <div>
              <strong>{item.name}</strong>
              {item.description ? <p>{item.description}</p> : null}
              {item.why ? <small><b>Why it helps:</b> {item.why}</small> : null}
              <button className="btn btn-sm btn-outline-primary mt-2" type="button" onClick={() => onAddSkill(item)}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>Add skill
              </button>
            </div>
          </article>)}
        </div>
      </section> : null}
    </div>
  );
};

const DraftRewriteReview = ({ proposal }) => (
  <section className="projects-skills-ai-draft-review">
    <strong>{proposal.kind === 'skill' ? 'Proposed skill wording' : 'Proposed project wording'}</strong>
    <dl>
      <div>
        <dt>{proposal.kind === 'skill' ? 'Name' : 'Title'}</dt>
        <dd>{proposal.name || proposal.title}</dd>
      </div>
      <div>
        <dt>Description</dt>
        <dd>{proposal.description || proposal.overview}</dd>
      </div>
      {proposal.kind === 'project' && Array.isArray(proposal.images) ? proposal.images.map((image) => (
        <div key={image.imageId}>
          <dt>Image description</dt>
          <dd>{image.description}</dd>
        </div>
      )) : null}
    </dl>
  </section>
);

const ProjectsSkillsAiAssistant = ({
  expanded,
  setExpanded,
  userId,
  initialTask = TASKS.RECOMMEND,
  preferredTask = TASKS.EDIT_PROJECTS,
  contextualTask,
  onGenerateRecommendations,
  onGenerateUpdates,
  onApplyUpdates,
  onAddRecommendedProject,
  onAddRecommendedSkill,
  onQueueStatusChange,
}) => {
  const [task, setTask] = useState(TASKS.RECOMMEND);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [historyReady, setHistoryReady] = useState(false);
  const requestQueue = useAiRequestQueue();
  const composerRef = useRef(null);
  const transcriptRef = useRef(null);
  const wasExpandedRef = useRef(false);
  const previousPreferredTaskRef = useRef(normalizeTask(preferredTask));
  const executingRequestRef = useRef(null);
  const resetRequestQueue = requestQueue.reset;

  useEffect(() => {
    resetRequestQueue();
  }, [resetRequestQueue, userId]);

  useEffect(() => {
    if (expanded && !wasExpandedRef.current) {
      setTask(normalizeTask(initialTask));
      setDraft('');
    }
    wasExpandedRef.current = expanded;
  }, [expanded, initialTask]);

  useEffect(() => {
    const nextPreferredTask = normalizeTask(preferredTask);
    if (nextPreferredTask === previousPreferredTaskRef.current) return;
    previousPreferredTaskRef.current = nextPreferredTask;

    const hasPendingDecision = messages.some((message) => !message.historical && (
      message.draftStatus === 'proposed' || message.updateStatus === 'proposed'
    ));
    if (expanded && !busy && !draft.trim() && !hasPendingDecision && !contextualTask) {
      setTask(nextPreferredTask);
    }
  }, [busy, contextualTask, draft, expanded, messages, preferredTask]);

  useEffect(() => {
    let active = true;
    setHistoryReady(false);
    loadProjectsSkillsAiHistory(userId)
      .then((saved) => { if (active) setMessages(saved); })
      .catch((error) => console.error('Error loading Projects and Skills AI history:', error))
      .finally(() => { if (active) setHistoryReady(true); });
    return () => { active = false; };
  }, [userId]);

  useEffect(() => {
    if (!historyReady || !userId) return undefined;
    const timer = setTimeout(() => {
      Promise.resolve(saveProjectsSkillsAiHistory(userId, messages)).catch((error) => (
        console.error('Error saving Projects and Skills AI history:', error)
      ));
    }, 400);
    return () => clearTimeout(timer);
  }, [historyReady, messages, userId]);

  useEffect(() => {
    onQueueStatusChange?.({
      count: requestQueue.requests.length,
      working: requestQueue.activeRequest?.status === AI_QUEUE_STATUS.RUNNING,
      needsAttention: requestQueue.needsAttention,
    });
  }, [
    onQueueStatusChange,
    requestQueue.activeRequest?.status,
    requestQueue.needsAttention,
    requestQueue.requests.length,
  ]);

  useEffect(() => {
    if (!expanded || busy) return;
    composerRef.current?.focus();
  }, [busy, expanded, task]);

  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    transcript.scrollTo?.({ top: transcript.scrollHeight, behavior: 'smooth' });
  }, [busy, messages]);

  const updateMessage = (id, updates) => setMessages((current) => current.map((message) => (
    message.id === id ? { ...message, ...updates } : message
  )));

  const finishDiscardedProposal = (messageId, updates) => {
    updateMessage(messageId, updates);
    if (requestQueue.activeRequest?.status === AI_QUEUE_STATUS.AWAITING_REVIEW) {
      requestQueue.complete(requestQueue.activeRequest.id);
    }
  };

  const executeRequest = async (queuedRequest) => {
    const request = queuedRequest.prompt;
    const requestedTask = queuedRequest.task.type;
    const prompt = String(request || '').trim();
    const activeTask = normalizeTask(requestedTask);
    setMessages((current) => [...current, newMessage('user', prompt, { task: activeTask })]);
    setBusy(true);
    try {
      if (activeTask === TASKS.REWRITE) {
        if (!contextualTask?.generate || !contextualTask?.onApply) {
          throw new Error('That form is no longer available. Open it again and retry.');
        }
        const proposal = await contextualTask.generate(prompt);
        setMessages((current) => [...current, newMessage(
          'assistant',
          'Review this rewrite. Applying it updates the open form; you can still edit it before saving.',
          {
            task: activeTask,
            draftProposal: { ...proposal, kind: contextualTask.kind },
            draftStatus: 'proposed',
            applyDraft: contextualTask.onApply,
          }
        )]);
        requestQueue.setStatus(queuedRequest.id, AI_QUEUE_STATUS.AWAITING_REVIEW);
      } else if (isEditTask(activeTask)) {
        const editScope = editScopeForTask(activeTask);
        const proposal = await onGenerateUpdates(prompt, editScope);
        if (!hasUpdates(proposal?.updates)) {
          setMessages((current) => [...current, newMessage(
            'assistant',
            'I could not find an approved project or skill wording change in that request. Name the saved item and what wording you want changed.',
            { task: activeTask }
          )]);
        } else {
          setMessages((current) => [...current, newMessage(
            'assistant',
            'Review these saved-data changes. Nothing is updated until you confirm.',
            {
              task: activeTask,
              updates: proposal.updates,
              updateContext: proposal.context,
              updateScope: proposal.scope || editScope,
              updateStatus: 'proposed',
            }
          )]);
          requestQueue.setStatus(queuedRequest.id, AI_QUEUE_STATUS.AWAITING_REVIEW);
        }
        if (!hasUpdates(proposal?.updates)) requestQueue.complete(queuedRequest.id);
      } else {
        const result = await onGenerateRecommendations(prompt);
        setMessages((current) => [...current, newMessage(
          'assistant',
          'Here are practical next steps based on your projects, skills, and target jobs.',
          { task: activeTask, recommendations: result }
        )]);
        requestQueue.complete(queuedRequest.id);
      }
    } catch (error) {
      const message = error?.message || 'The AI assistant could not complete that request. Please try again.';
      setMessages((current) => [...current, newMessage(
        'error',
        message,
        { task: activeTask }
      )]);
      requestQueue.setStatus(queuedRequest.id, AI_QUEUE_STATUS.FAILED, { error: message });
    } finally {
      setBusy(false);
    }
  };

  const send = (request, requestedTask = task) => {
    const prompt = String(request || '').trim();
    const activeTask = normalizeTask(requestedTask);
    if ((!prompt && activeTask !== TASKS.RECOMMEND) || !historyReady) return;
    requestQueue.enqueue({
      type: activeTask,
      key: activeTask,
      label: TASK_OPTIONS.find((option) => option.key === activeTask)?.label
        || contextualTask?.label,
      contextLabel: activeTask === TASKS.REWRITE ? contextualTask?.contextLabel : '',
    }, prompt || DEFAULT_NEXT_STEPS_REQUEST);
    setDraft('');
    setTask(normalizeTask(preferredTask));
  };

  useEffect(() => {
    if (busy || requestQueue.activeRequest || !requestQueue.nextRequest || !historyReady) return;
    requestQueue.start(requestQueue.nextRequest.id);
  }, [busy, historyReady, requestQueue]);

  useEffect(() => {
    const request = requestQueue.activeRequest;
    if (!request || request.status !== AI_QUEUE_STATUS.RUNNING || busy) return;
    if (executingRequestRef.current === request.id) return;
    executingRequestRef.current = request.id;
    void executeRequest(request).finally(() => {
      executingRequestRef.current = null;
    });
    // executeRequest resolves feature callbacks from the latest mounted form and tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, requestQueue.activeRequest?.id, requestQueue.activeRequest?.status]);

  const applyDraftRewrite = (message) => {
    if (busy || message.historical || message.draftStatus !== 'proposed') return;
    message.applyDraft?.(message.draftProposal);
    updateMessage(message.id, { draftStatus: 'applied', applyDraft: undefined });
    setMessages((current) => [...current, newMessage(
      'assistant',
      'The rewrite was added to the form. Review it there before saving.',
      { task: TASKS.REWRITE }
    )]);
    if (requestQueue.activeRequest?.status === AI_QUEUE_STATUS.AWAITING_REVIEW) {
      requestQueue.complete(requestQueue.activeRequest.id);
    }
  };

  const applyUpdates = async (message) => {
    if (busy || message.historical || message.updateStatus !== 'proposed') return;
    setBusy(true);
    try {
      await onApplyUpdates({
        updates: message.updates,
        context: message.updateContext,
        ...(message.updateScope ? { scope: message.updateScope } : {}),
      });
      updateMessage(message.id, { updateStatus: 'applied' });
      setMessages((current) => [...current, newMessage(
        'assistant',
        message.updateScope === 'projects'
          ? 'The project changes were saved successfully.'
          : message.updateScope === 'skills'
            ? 'The skill changes were saved successfully.'
            : 'The project and skill changes were saved successfully.',
        { task: message.updateScope === 'skills' ? TASKS.EDIT_SKILLS : TASKS.EDIT_PROJECTS }
      )]);
      if (requestQueue.activeRequest?.status === AI_QUEUE_STATUS.AWAITING_REVIEW) {
        requestQueue.complete(requestQueue.activeRequest.id);
      }
    } catch (error) {
      setMessages((current) => [...current, newMessage(
        'error',
        error?.message || 'Those saved changes could not be applied. Please try again.',
        { task: message.updateScope === 'skills' ? TASKS.EDIT_SKILLS : TASKS.EDIT_PROJECTS }
      )]);
    } finally {
      setBusy(false);
    }
  };

  const availableTasks = [
    ...TASK_OPTIONS,
    ...(contextualTask ? [{
      key: TASKS.REWRITE,
      label: contextualTask.label,
      iconClass: 'bi-stars',
      className: 'projects-skills-ai-contextual-task',
      promptRequired: true,
    }] : []),
  ];

  return (
    <AiAssistantMenuShell title="Portfolio Context AI"
      className="projects-skills-ai-menu" expanded={expanded}
      onClose={() => setExpanded(false)} busy={busy}>
      <div className="projects-skills-ai-shell">
        <div className="projects-skills-ai-transcript" ref={transcriptRef} role="log"
          aria-live="polite" aria-label="Portfolio Context AI conversation">
          {!historyReady ? <div className="projects-skills-ai-loading">
            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span className="visually-hidden">Loading conversation</span>
          </div> : null}
          {historyReady && !messages.length ? <div className="projects-skills-ai-welcome">
            <i className="bi bi-stars" aria-hidden="true"></i>
            <strong>Plan your next career step</strong>
            <p>Get ideas for your target jobs or safely improve wording in saved projects and skills.</p>
          </div> : null}

          {messages.map((message) => <article className={`projects-skills-ai-message is-${message.role}`} key={message.id}>
            <span className="projects-skills-ai-avatar" aria-hidden="true"><i className={`bi ${
              message.role === 'user' ? 'bi-person-fill' : message.role === 'error' ? 'bi-exclamation-lg' : 'bi-stars'
            }`}></i></span>
            <div>
              {message.content ? <p>{message.content}</p> : null}
              {message.recommendations ? <Recommendations
                value={message.recommendations}
                onAddProject={onAddRecommendedProject}
                onAddSkill={onAddRecommendedSkill}
              /> : null}
              {message.draftProposal ? <DraftRewriteReview proposal={message.draftProposal} /> : null}
              {message.draftProposal && message.draftStatus === 'proposed' && !message.historical ? (
                <AiAssistantDecisionActions ariaLabel="Rewrite proposal actions" disabled={busy} actions={[
                  { key: 'discard', label: 'Discard', iconClass: 'bi-x-lg', onClick: () => finishDiscardedProposal(message.id, { draftStatus: 'discarded', applyDraft: undefined }) },
                  { key: 'apply', label: 'Apply to form', iconClass: 'bi-check2', variant: 'primary', onClick: () => applyDraftRewrite(message) },
                ]} />
              ) : null}
              {message.draftProposal && (message.historical || message.draftStatus !== 'proposed') ? (
                <span className={`projects-skills-ai-update-status is-${message.draftStatus}`}>
                  {message.draftStatus === 'applied'
                    ? 'Applied to form'
                    : message.draftStatus === 'discarded' ? 'Discarded' : 'Previous proposal'}
                </span>
              ) : null}
              {message.updates ? <AiRecordUpdateReview
                updates={message.updates}
                context={message.updateContext || { projects: [], skills: [] }}
              /> : null}
              {message.updates && message.updateStatus === 'proposed' && !message.historical ? (
                <AiAssistantDecisionActions ariaLabel="Saved data update actions" disabled={busy} actions={[
                  { key: 'discard', label: 'Discard', iconClass: 'bi-x-lg', onClick: () => finishDiscardedProposal(message.id, { updateStatus: 'discarded' }) },
                  { key: 'apply', label: 'Apply changes', iconClass: 'bi-check2', variant: 'primary', onClick: () => void applyUpdates(message) },
                ]} />
              ) : null}
              {message.updates && (message.historical || message.updateStatus !== 'proposed') ? (
                <span className={`projects-skills-ai-update-status is-${message.updateStatus}`}>
                  {message.updateStatus === 'applied' ? 'Applied' : message.updateStatus === 'discarded' ? 'Discarded' : 'Previous proposal'}
                </span>
              ) : null}
            </div>
          </article>)}

          {busy ? <article className="projects-skills-ai-message is-assistant is-working">
            <span className="projects-skills-ai-avatar"><span className="spinner-border spinner-border-sm"></span></span>
            <div><p>{requestQueue.activeRequest?.task.type === TASKS.REWRITE
              ? 'Preparing a rewrite for the open form...'
              : isEditTask(requestQueue.activeRequest?.task.type)
                ? 'Preparing safe saved-data changes...'
                : 'Comparing your work with your target jobs...'}</p></div>
          </article> : null}

        </div>

        <AiAssistantQueue
          requests={requestQueue.requests}
          tasks={availableTasks}
          disabled={!historyReady}
          onUpdate={requestQueue.update}
          onRemove={requestQueue.remove}
          onRetry={requestQueue.retry}
          onMove={requestQueue.move}
          onMoveBy={requestQueue.moveBy}
        />

        <AiAssistantTaskPicker
          className="projects-skills-ai-task-options"
          tasks={availableTasks}
          activeTask={task}
          onSelect={(nextTask) => {
            setDraft('');
            if (nextTask === TASKS.RECOMMEND) {
              void send(DEFAULT_NEXT_STEPS_REQUEST, TASKS.RECOMMEND);
              return;
            }
            setTask(nextTask);
          }}
          disabled={!historyReady}
        />

        <AiAssistantComposer ref={composerRef} draft={draft}
          taskLabel={task === TASKS.REWRITE
            ? contextualTask?.label || 'Rewrite form'
            : task === TASKS.EDIT_PROJECTS
              ? 'Edit projects'
              : task === TASKS.EDIT_SKILLS ? 'Edit skills'
              : 'Recommend next steps'}
          contextLabel={task === TASKS.REWRITE
            ? contextualTask?.contextLabel
            : task === TASKS.EDIT_PROJECTS
              ? 'Only approved project fields can change'
              : task === TASKS.EDIT_SKILLS ? 'Only approved skill fields can change'
              : 'Uses your current projects, skills, and target jobs'}
          placeholder={task === TASKS.REWRITE
            ? contextualTask?.placeholder || 'Describe how the wording should improve...'
            : task === TASKS.EDIT_PROJECTS
              ? 'Example: Improve the description of my booking project...'
              : task === TASKS.EDIT_SKILLS ? 'Example: Make my React skill description clearer...'
              : 'Ask what project or skill you should work on next...'}
          busy={false} disabled={!historyReady} onDraftChange={setDraft} onSubmit={send} />
      </div>
    </AiAssistantMenuShell>
  );
};

Recommendations.propTypes = {
  value: PropTypes.object,
  onAddProject: PropTypes.func.isRequired,
  onAddSkill: PropTypes.func.isRequired,
};
DraftRewriteReview.propTypes = { proposal: PropTypes.object.isRequired };
ProjectsSkillsAiAssistant.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  userId: PropTypes.string,
  initialTask: PropTypes.oneOf(Object.values(TASKS)),
  preferredTask: PropTypes.oneOf([TASKS.RECOMMEND, TASKS.EDIT_PROJECTS, TASKS.EDIT_SKILLS]),
  contextualTask: PropTypes.shape({
    kind: PropTypes.oneOf(['project', 'skill']).isRequired,
    label: PropTypes.string.isRequired,
    contextLabel: PropTypes.string,
    placeholder: PropTypes.string,
    generate: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
  }),
  onGenerateRecommendations: PropTypes.func.isRequired,
  onGenerateUpdates: PropTypes.func.isRequired,
  onApplyUpdates: PropTypes.func.isRequired,
  onAddRecommendedProject: PropTypes.func.isRequired,
  onAddRecommendedSkill: PropTypes.func.isRequired,
  onQueueStatusChange: PropTypes.func,
};

export default ProjectsSkillsAiAssistant;
